import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { BackendStorageService, PlaylistGenerationSettings } from './backend-storage.service';

@Injectable({
  providedIn: 'root'
})
export class PlaylistGenerationSettingsService {
  private settingsSubject = new BehaviorSubject<PlaylistGenerationSettings | null>(null);
  
  // Observable for components to subscribe to
  settings$ = this.settingsSubject.asObservable();

  // Default values (matches backend defaults)
  private readonly DEFAULT_SETTINGS: PlaylistGenerationSettings = {
    playlistName: 'Dynamic Playlist',
    playlistLength: 30,
    selectedGenres: [],
    historyFilterDays: 7,
    maximumPlayCount: 2,
    popularity: { min: 20, max: 90 },
    yearRange: undefined,
    excludeRecentTracks: true,
    excludeDislikedArtists: true,
  };

  constructor(private backendStorage: BackendStorageService) {
    this.loadSettings();
  }

  /**
   * Load settings from backend
   */
  private loadSettings(): void {
    this.backendStorage.getPlaylistSettings().pipe(
      catchError(() => {
        console.warn('Failed to load playlist settings, using defaults');
        return of(this.DEFAULT_SETTINGS);
      })
    ).subscribe(settings => {
      this.settingsSubject.next(settings);
    });
  }

  /**
   * Get current settings (synchronous)
   */
  getCurrentSettings(): PlaylistGenerationSettings {
    return this.settingsSubject.value || this.DEFAULT_SETTINGS;
  }

  /**
   * Get settings as observable
   */
  getSettings(): Observable<PlaylistGenerationSettings> {
    if (this.settingsSubject.value) {
      return of(this.settingsSubject.value);
    }
    return this.backendStorage.getPlaylistSettings().pipe(
      tap(settings => this.settingsSubject.next(settings)),
      catchError(() => {
        const defaults = this.DEFAULT_SETTINGS;
        this.settingsSubject.next(defaults);
        return of(defaults);
      })
    );
  }

  /**
   * Update specific settings
   */
  updateSettings(updates: Partial<PlaylistGenerationSettings>): Observable<void> {
    const currentSettings = this.getCurrentSettings();
    const updatedSettings = { ...currentSettings, ...updates };
    
    return this.backendStorage.updatePlaylistSettings(updates).pipe(
      tap(() => {
        console.log('PlaylistGenerationSettingsService: Emitting updated settings:', updatedSettings);
        this.settingsSubject.next(updatedSettings);
      })
    );
  }

  /**
   * Update playlist name
   */
  updatePlaylistName(name: string): Observable<void> {
    return this.updateSettings({ playlistName: name });
  }

  /**
   * Update playlist length
   */
  updatePlaylistLength(length: number): Observable<void> {
    return this.updateSettings({ playlistLength: Math.max(1, Math.min(100, length)) });
  }

  /**
   * Update selected genres
   */
  updateSelectedGenres(genres: string[]): Observable<void> {
    return this.updateSettings({ selectedGenres: genres });
  }

  /**
   * Update filter settings (consolidated from multiple components)
   */
  updateFilterSettings(updates: {
    historyFilterDays?: number;
    maximumPlayCount?: number;
    excludeRecentTracks?: boolean;
    excludeDislikedArtists?: boolean;
  }): Observable<void> {
    return this.updateSettings(updates);
  }

  /**
   * Update advanced filters
   */
  updateAdvancedFilters(updates: {
    popularity?: { min: number; max: number };
    yearRange?: { min: number; max: number };
  }): Observable<void> {
    return this.updateSettings(updates);
  }

  /**
   * Reset all settings to defaults
   */
  resetToDefaults(): Observable<void> {
    return this.backendStorage.updatePlaylistSettings(this.DEFAULT_SETTINGS).pipe(
      tap(() => {
        this.settingsSubject.next(this.DEFAULT_SETTINGS);
      })
    );
  }

  /**
   * Get settings for specific use cases
   */
  
  // For QuickPlay component
  getQuickPlaySettings(): Observable<{
    playlistName: string;
    playlistLength: number;
    selectedGenres: string[];
    historyFilterDays: number;
    maximumPlayCount: number;
  }> {
    return this.settings$.pipe(
      map(settings => ({
        playlistName: settings?.playlistName ?? this.DEFAULT_SETTINGS.playlistName,
        playlistLength: settings?.playlistLength ?? this.DEFAULT_SETTINGS.playlistLength,
        selectedGenres: settings?.selectedGenres ?? this.DEFAULT_SETTINGS.selectedGenres,
        historyFilterDays: settings?.historyFilterDays ?? this.DEFAULT_SETTINGS.historyFilterDays,
        maximumPlayCount: settings?.maximumPlayCount ?? this.DEFAULT_SETTINGS.maximumPlayCount
      }))
    );
  }

  // For playlist filtering (used by playlist detail components)
  getFilterSettings(): Observable<{
    historyFilterDays: number;
    maximumPlayCount: number;
    excludeRecentTracks: boolean;
    excludeDislikedArtists: boolean;
  }> {
    return this.settings$.pipe(
      map(settings => ({
        historyFilterDays: settings?.historyFilterDays ?? this.DEFAULT_SETTINGS.historyFilterDays,
        maximumPlayCount: settings?.maximumPlayCount ?? this.DEFAULT_SETTINGS.maximumPlayCount,
        excludeRecentTracks: settings?.excludeRecentTracks ?? this.DEFAULT_SETTINGS.excludeRecentTracks,
        excludeDislikedArtists: settings?.excludeDislikedArtists ?? this.DEFAULT_SETTINGS.excludeDislikedArtists
      }))
    );
  }

  // For advanced filtering features
  getAdvancedFilterSettings(): Observable<{
    popularity?: { min: number; max: number };
    yearRange?: { min: number; max: number };
  }> {
    return this.settings$.pipe(
      map(settings => ({
        popularity: settings?.popularity,
        yearRange: settings?.yearRange
      }))
    );
  }

  /**
   * Validate settings before saving
   */
  private validateSettings(settings: Partial<PlaylistGenerationSettings>): boolean {
    if (settings.playlistLength !== undefined) {
      if (settings.playlistLength < 1 || settings.playlistLength > 100) {
        return false;
      }
    }
    
    if (settings.historyFilterDays !== undefined) {
      if (settings.historyFilterDays < 0 || settings.historyFilterDays > 365) {
        return false;
      }
    }
    
    if (settings.maximumPlayCount !== undefined) {
      if (settings.maximumPlayCount < 1 || settings.maximumPlayCount > 10) {
        return false;
      }
    }
    
    if (settings.popularity !== undefined) {
      if (settings.popularity.min < 0 || settings.popularity.max > 100 || 
          settings.popularity.min >= settings.popularity.max) {
        return false;
      }
    }
    
    if (settings.yearRange !== undefined) {
      const currentYear = new Date().getFullYear();
      if (settings.yearRange.min < 1900 || settings.yearRange.max > currentYear ||
          settings.yearRange.min >= settings.yearRange.max) {
        return false;
      }
    }
    
    return true;
  }

  /**
   * Get validation errors for settings
   */
  getValidationErrors(settings: Partial<PlaylistGenerationSettings>): string[] {
    const errors: string[] = [];
    
    if (settings.playlistLength !== undefined) {
      if (settings.playlistLength < 1 || settings.playlistLength > 100) {
        errors.push('Playlist length must be between 1 and 100');
      }
    }
    
    if (settings.historyFilterDays !== undefined) {
      if (settings.historyFilterDays < 0 || settings.historyFilterDays > 365) {
        errors.push('History filter days must be between 0 and 365');
      }
    }
    
    if (settings.maximumPlayCount !== undefined) {
      if (settings.maximumPlayCount < 1 || settings.maximumPlayCount > 10) {
        errors.push('Maximum play count must be between 1 and 10');
      }
    }
    
    if (settings.popularity !== undefined) {
      if (settings.popularity.min < 0 || settings.popularity.max > 100) {
        errors.push('Popularity values must be between 0 and 100');
      }
      if (settings.popularity.min >= settings.popularity.max) {
        errors.push('Popularity minimum must be less than maximum');
      }
    }
    
    if (settings.yearRange !== undefined) {
      const currentYear = new Date().getFullYear();
      if (settings.yearRange.min < 1900 || settings.yearRange.max > currentYear) {
        errors.push(`Year range must be between 1900 and ${currentYear}`);
      }
      if (settings.yearRange.min >= settings.yearRange.max) {
        errors.push('Year range minimum must be less than maximum');
      }
    }
    
    return errors;
  }
}